'use client';

import Script from 'next/script';
import { useEffect, ReactNode } from 'react';
import { env } from 'next-runtime-env';

declare global {
  interface Window {
    OneTrustConsentManager?: {
      start: (config: {
        applicationName: string;
        writeKey: string;
        enableSegmentCookieSync: boolean;
      }) => Promise<void>;
    };
    OptanonActiveGroups?: string;
  }
}

const getActiveGroups = () =>
  window.OptanonActiveGroups?.split(',').filter(Boolean) ?? [];

const useReloadOnConsentWithdrawal = () => {
  useEffect(() => {
    const grantedGroups = new Set(getActiveGroups());

    const trackGrantedGroups = () =>
      getActiveGroups().forEach((group) => grantedGroups.add(group));

    // Running scripts can't be unloaded, so OneTrust's documented opt-out handling is a page refresh.
    // Checked only on OTConsentApplied (user consent actions) so load-time group changes can't cause reload loops.
    const reloadIfConsentWithdrawn = () => {
      const activeGroups = getActiveGroups();
      if ([...grantedGroups].some((group) => !activeGroups.includes(group))) {
        window.location.reload();
      }
    };

    window.addEventListener('OneTrustGroupsUpdated', trackGrantedGroups);
    window.addEventListener('OTConsentApplied', reloadIfConsentWithdrawn);

    return () => {
      window.removeEventListener('OneTrustGroupsUpdated', trackGrantedGroups);
      window.removeEventListener('OTConsentApplied', reloadIfConsentWithdrawn);
    };
  }, []);
};

export const OneTrustConsentManager = (): ReactNode => {
  const onetrustId = env('NEXT_PUBLIC_ONETRUST_DOMAIN_SCRIPT');
  const segmentKey = env('NEXT_PUBLIC_SEGMENT_KEY');

  useReloadOnConsentWithdrawal();

  useEffect(() => {
    if (!onetrustId || !segmentKey) return;

    let retryCount = 0;
    const MAX_RETRIES = 40;

    const initializeConsentManager = () => {
      if (window.OneTrustConsentManager?.start) {
        window.OneTrustConsentManager.start({
          applicationName: 'oura-business-site',
          writeKey: segmentKey,
          enableSegmentCookieSync: true,
        });
      } else if (retryCount < MAX_RETRIES) {
        retryCount++;
        setTimeout(initializeConsentManager, 50);
      } else {
        console.error('OneTrustConsentManager failed to load');
      }
    };

    initializeConsentManager();
  }, [onetrustId, segmentKey]);

  // Don't render if OneTrust or Segment is not configured (e.g., local dev)
  if (!onetrustId || !segmentKey) {
    return null;
  }

  return (
    <>
      {/* OptanonWrapper function required by OneTrust */}
      <Script
        id="onetrust-optanon-wrapper"
        strategy="beforeInteractive"
        dangerouslySetInnerHTML={{ __html: 'function OptanonWrapper() {}' }}
      />

      {/* OneTrust Domain Script */}
      <Script
        id="onetrust-domain-script"
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        data-domain-script={onetrustId}
        strategy="afterInteractive"
      />

      {/* OneTrust Consent Manager UMD */}
      <Script
        id="onetrust-consent-manager"
        src="//ouraring.com/b/a/onetrust-consent-manager/2.4.1/onetrust-consent-manager.min.js"
        strategy="afterInteractive"
        defer
      />
    </>
  );
};
